<template>
  <div class="auth-page">
    <div class="auth-shell">
      <div class="auth-shell__brand">
        <div class="auth-brand__header">
          <div class="auth-brand__logo">
            <img src="@/assets/logo.png" alt="BISU RIE System logo" />
          </div>
          <div class="auth-brand__label">
            <span>Digital Platform</span>
            <span>RIE System</span>
          </div>
        </div>

        <div>
          <h1 class="auth-brand__title">
            <span>Research, Innovation</span>
            <span class="accent">& Extension</span>
          </h1>
          <p class="auth-brand__description">
            Empowering BISU faculty to track, manage, and showcase research outputs, projects, and extension activities.
          </p>
        </div>

        <div class="auth-brand__stats">
          <div class="auth-brand__stat">
            <span class="auth-brand__stat-figure">500+</span>
            <span class="auth-brand__stat-caption">Research Papers</span>
          </div>
          <div class="auth-brand__stat">
            <span class="auth-brand__stat-figure">150+</span>
            <span class="auth-brand__stat-caption">Faculty Members</span>
          </div>
          <div class="auth-brand__stat">
            <span class="auth-brand__stat-figure">80+</span>
            <span class="auth-brand__stat-caption">Extension Programs</span>
          </div>
        </div>
      </div>

      <div class="auth-shell__content">
        <form class="auth-form" @submit.prevent="handleSignIn">
          <button class="auth-arrow-link" type="button" @click="goHome">← Back to home</button>

          <div class="auth-form__header">
            <h2 class="main-title">Welcome back</h2>
            <p class="subtitle">Sign in to access your RIE dashboard.</p>
          </div>

          <div class="form-group">
            <label class="form-label" for="username">Username</label>
            <input
              type="text"
              id="username"
              class="form-input"
              v-model="form.username"
              placeholder="Enter your username"
              :class="{ 'error': errors.username }"
            />
            <span v-if="errors.username" class="small-note error-message">{{ errors.username }}</span>
          </div>

          <div class="form-group">
            <label class="form-label" for="password">Password</label>
            <div class="password-field">
              <input
                :type="showPassword ? 'text' : 'password'"
                id="password"
                class="form-input"
                v-model="form.password"
                placeholder="Enter your password"
                :class="{ 'error': errors.password }"
                autocomplete="current-password"
              />
              <button
                type="button"
                class="password-toggle"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                :aria-pressed="showPassword ? 'true' : 'false'"
                @click="showPassword = !showPassword"
              >
                <svg v-if="!showPassword" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M12 5c-5.5 0-9.5 4.2-11 7 1.5 2.8 5.5 7 11 7s9.5-4.2 11-7c-1.5-2.8-5.5-7-11-7zm0 12c-2.8 0-5-2.2-5-5s2.2-5 5-5 5 2.2 5 5-2.2 5-5 5zm0-8a3 3 0 100 6 3 3 0 000-6z"
                  />
                </svg>
                <svg v-else viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M2.3 3.7a1 1 0 011.4 0l16.6 16.6a1 1 0 01-1.4 1.4l-2.2-2.2c-1.4.7-3 .9-4.7.9-5.5 0-9.5-4.2-11-7 1-1.9 3.2-4.6 6.4-6.1L2.3 5.1a1 1 0 010-1.4zM12 7c-.8 0-1.6.2-2.3.6l1.7 1.7c.2-.1.4-.1.6-.1 1.7 0 3 1.3 3 3 0 .2 0 .4-.1.6l1.7 1.7c.4-.7.6-1.5.6-2.3 0-2.8-2.2-5-5-5zm0-2c5.5 0 9.5 4.2 11 7-.7 1.3-1.8 2.8-3.3 4.1l-1.4-1.4c1.1-1 2-2.1 2.6-2.7-1.5-2.2-4.9-5-8.9-5-1 0-2 .2-2.9.5L7.8 6.1c1.3-.7 2.7-1.1 4.2-1.1zm-8.9 7c1.5 2.2 4.9 5 8.9 5 1.1 0 2.2-.2 3.2-.6l-1.6-1.6c-.5.1-1 .2-1.6.2-2.8 0-5-2.2-5-5 0-.6.1-1.1.2-1.6L5 6.8c-1.2 1-2.1 2.2-2.7 3.2z"
                  />
                </svg>
              </button>
            </div>
            <span v-if="errors.password" class="small-note error-message">{{ errors.password }}</span>
          </div>

          <button type="submit" class="custom-button primary" :disabled="isLoading">
            <span v-if="isLoading" class="spinner"></span>
            {{ isLoading ? 'Signing In...' : 'Sign In' }}
          </button>

          <div class="auth-form__meta">
            <a href="#" @click.prevent="handleForgotPassword">Forgot password?</a>
            <a href="#" @click.prevent="handleSignUp">Create account</a>
          </div>

          <div v-if="error || success" class="form-feedback">
            <div v-if="error" class="error-message">{{ error }}</div>
            <div v-if="success" class="success-message">{{ success }}</div>
          </div>

        </form>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/apiClient'

export default {
  name: 'SignIn',
  data() {
    return {
      form: { username: '', password: '' },
      errors: { username: '', password: '' },
      error: '',
      success: '',
      isLoading: false,
      showPassword: false
    }
  },
  methods: {
    isAccountApproved(user) {
      if (!user || typeof user !== 'object') return false

      // Support multiple backend naming conventions for account approval.
      if (Object.prototype.hasOwnProperty.call(user, 'approved')) return !!user.approved
      if (Object.prototype.hasOwnProperty.call(user, 'is_approved')) return !!user.is_approved
      if (Object.prototype.hasOwnProperty.call(user, 'isApproved')) return !!user.isApproved

      const rawStatus = (user.account_status ?? user.status ?? '').toString().trim().toLowerCase()
      if (rawStatus) {
        const blockedStatuses = ['pending', 'for approval', 'unapproved', 'rejected', 'inactive', 'disabled']
        if (blockedStatuses.includes(rawStatus)) return false
      }

      // Backward compatibility: if no explicit flag/status is sent, keep existing login behavior.
      return true
    },
    clearErrors() {
      this.errors.username = '';
      this.errors.password = '';
      this.error = '';
      this.success = '';
    },
    validateForm() {
      this.clearErrors();
      let isValid = true;
      if (!this.form.username.trim()) { this.errors.username = 'Username is required'; isValid = false; }
      if (!this.form.password.trim()) { this.errors.password = 'Password is required'; isValid = false; }
      else if (this.form.password.length < 6) { this.errors.password = 'Password must be at least 6 characters'; isValid = false; }
      return isValid;
    },
    async handleSignIn() {
  if (!this.validateForm()) return;
  this.isLoading = true;
  this.clearErrors();

  try {
    const { data } = await api.post('/login', this.form)

    if (data.success) {
  if (!this.isAccountApproved(data.user)) {
    const pendingMessage = 'Your account is not yet approved.'
    this.error = pendingMessage
    alert(pendingMessage)
    return
  }

  localStorage.setItem('authToken', 'fake-jwt-token');
  localStorage.setItem('user', JSON.stringify(data.user));

  const role = data.user.role; // get user role
  this.success = 'Login successful! Redirecting...';

  setTimeout(() => {
    if (role === 'admin') {
      this.$router.push('/admin/dashboard');
    } else if (role === 'faculty') {
      this.$router.push('/faculty/dashboard');
    } else {
      this.error = 'Unknown user role';
    }
  }, 1000);
} else {
  this.error = data.message || 'Invalid credentials';
}
  } catch (err) {
    const status = err?.response?.status;
    const apiMessage = err?.response?.data?.message || err?.response?.data?.error;
    const isNetworkError = !err?.response;
    const normalizedApiMessage = typeof apiMessage === 'string' ? apiMessage.trim().toLowerCase() : '';
    const isPendingApprovalMessage = normalizedApiMessage.includes('not yet approved')
      || normalizedApiMessage.includes('pending approval')
      || normalizedApiMessage.includes('awaiting approval');

    if (status === 403 || isPendingApprovalMessage) {
      const pendingMessage = 'Your account is not yet approved.';
      this.error = pendingMessage;
      alert(pendingMessage);
    } else if (status === 401) {
      this.error = 'Wrong username or password.';
    } else if (status === 404) {
      this.error = 'Account not found.';
    } else if (isNetworkError) {
      this.error = 'Unable to reach server. Please check your connection or CORS settings.';
    } else if (typeof apiMessage === 'string' && apiMessage.trim()) {
      this.error = apiMessage.trim();
    } else {
      this.error = 'Sign-in failed. Please try again.';
    }
  } finally {
    this.isLoading = false;
  }
},

    handleSignUp() { this.$router.push({ name: 'SignUp' }); },
    handleForgotPassword() { this.$router.push({ name: 'ForgotPassword' }); },
    goHome() { this.$router.push('/'); }
  }
}
</script>

<style scoped>
.auth-shell__content {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: clamp(2.5rem, 7vw, 5rem) clamp(1.25rem, 4vw, 3.5rem);
}

.auth-form {
  width: min(100%, 680px);
  margin: 0 auto;
  padding: 0;
  background: none;
  border: none;
  box-shadow: none;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  opacity: 1;
  transform: none;
  animation: none;
}

.auth-form__header .main-title {
  font-size: clamp(2.1rem, 3.8vw, 3rem);
  line-height: 1.05;
  margin-bottom: 0.35rem;
  color: #f8fbff;
  text-shadow: none;
}

.auth-form__header .subtitle {
  font-size: 0.95rem;
  color: rgba(186, 201, 230, 0.9);
  margin-top: 0;
}

.form-label {
  color: rgba(214, 226, 250, 0.9);
}

.auth-form .form-input {
  background: rgba(10, 22, 40, 0.72);
  border: 1px solid rgba(83, 112, 160, 0.45);
  color: rgba(235, 242, 255, 0.92);
}

.auth-form .form-input::placeholder {
  color: rgba(186, 201, 230, 0.55);
}

.auth-form .form-input:focus {
  border-color: rgba(56, 189, 248, 0.78);
  box-shadow: 0 0 0 2px rgba(56, 189, 248, 0.24);
  background: rgba(14, 27, 45, 0.9);
}

.password-field {
  position: relative;
  width: 100%;
}

.password-field .form-input {
  width: 100%;
  padding-right: 2.75rem;
}

.password-toggle {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  display: inline-grid;
  place-items: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: rgba(226, 232, 255, 0.7);
  cursor: pointer;
  line-height: 0;
  z-index: 2;
}

.password-toggle:hover {
  color: #eff6ff;
}

.password-toggle:focus-visible {
  outline: 2px solid rgba(125, 211, 252, 0.55);
  outline-offset: 2px;
}

.auth-form__meta,
.auth-form__links {
  color: rgba(186, 201, 230, 0.72);
}

.auth-form__meta a,
.auth-form__links a {
  color: #7dd3fc;
  font-weight: 600;
}

.auth-form__meta a:hover,
.auth-form__links a:hover {
  color: #bae6fd;
}

.custom-button.primary {
  box-shadow: 0 18px 34px rgba(14, 116, 144, 0.35);
}

.custom-button.primary {
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.custom-button.primary:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 10px 24px rgba(56, 189, 248, 0.16);
}

.custom-button.primary:active:not(:disabled) {
  transform: translateY(-1px) scale(0.995);
}

/* Loading spinner */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.18);
  border-top-color: #38bdf8;
  animation: spin 0.9s linear infinite;
  margin-right: 8px;
  vertical-align: middle;
}

@keyframes spin { to { transform: rotate(360deg); } }

@media (prefers-reduced-motion: reduce) {
  .auth-form,
  .custom-button.primary {
    animation: none !important;
    transition: none !important;
    transform: none !important;
    opacity: 1 !important;
  }
}

@media (max-width: 640px) {
  .auth-shell__content {
    align-items: center;
    padding: clamp(2rem, 9vw, 3rem) clamp(1rem, 6vw, 1.5rem);
  }

  .auth-form__header .main-title {
    font-size: clamp(1.9rem, 8vw, 2.3rem);
  }
}
</style>


